import { useState } from 'react'

import { ActStrip, useConfirmAfterApplied } from './ActStrip.jsx'
import { seriesSeasonsOf, subscriptionsOf } from './watch.js'
import { KIND_UNLINK_SEASON, unlinkCommand, useWatchRequest } from './watchRequests.js'

// P6-298f -- "clean up a series from the website." The site's own half of
// `/watch unlink` (take a season out of its series -- P6-298d): applied
// through `cogs/watch/requests.py::_evaluate_unlink_season`, which calls the
// SAME store-touching code the Discord command does
// (`cogs/watch/helpers.py::apply_unlink`) -- never a second implementation.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 REMOVE WAS PULLED IN THE FIX ROUND -- THIS FILE ONCE ALSO HAD IT
// ═══════════════════════════════════════════════════════════════════════════
//
// The first draft of this slice gave the title page a "Remove from the
// list" control too (`watch.remove_title`, mirroring `/watch remove`).
// ADR-0009 line 10 records Jordan's own ruling -- "removal stays
// command-only (2026-09-20); drop likewise (2026-09-22)" -- restated as D6,
// "Remove and drop stay commands, a deliberate surface for a verdict." A
// later, unrelated remark about clean-up being "easy" does not override an
// explicit, dated ruling in an accepted ADR, so remove was pulled entirely:
// no kind, no control, no choice UI, no confirm copy. Unlink was never
// covered by that ruling at all (it deletes nothing, and is the direct
// structural inverse of `watch.link_sequel`, already a request kind since
// V2-6h1) -- it needed no override and is everything this file now builds.
// See `docs/reports/P6-298f.md` for the fix-round detail; the pulled remove
// control was built and tested at commit `b0cfc7d`.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 KEPT LOW-KEY, BELOW THE CONTENT
// ═══════════════════════════════════════════════════════════════════════════
//
// Mounted at the very bottom of the page (`WatchTitle.jsx`), reusing
// `FranchiseLinkControl.jsx`'s own CSS (`d2d-watchtitle__franchiselink*`) --
// this slice does not own `app.css`, and that control's plain, small,
// bordered-top box is exactly the visual weight a "manage" section wants.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NO MANAGE SECTION AT ALL WHEN THERE IS NOTHING TO MANAGE
// ═══════════════════════════════════════════════════════════════════════════
//
// With only unlink left, the whole section is pointless on a title that
// isn't linked to anything -- an empty "Manage" box with nothing inside it
// would be worse than no box. `ManagePanel` renders nothing at all unless
// `isLinkedToSomething(series)`, on BOTH the listed title's own page and
// P6-298b's unlisted-season view.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 SIGNED OUT — THE WHOLE PANEL, MATCHING EVERY OTHER REQUEST CONTROL
// ═══════════════════════════════════════════════════════════════════════════
//
// `FranchiseLinkControl.jsx`'s own header states this page's convention:
// hide the whole control, never a read-only remainder. `you.signedIn` gates
// the entire panel below, not just its buttons.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE NEIGHBOUR NAMES ARE DISPLAY ONLY
// ═══════════════════════════════════════════════════════════════════════════
//
// `neighborsOf` below mirrors `cogs/watch/helpers.py::apply_unlink`'s own
// neighbour walk, from the SAME `series` payload the season-tab strip
// already reads -- but the applier re-derives it independently at apply
// time (`cogs/watch/requests.py::_evaluate_unlink_season`), so a stale
// neighbour name here can only ever change what the CONFIRM STEP says,
// never what actually happens to the store.

const UNLINK_DONE_TEXT = 'Out of its series.'

/** Is this season linked to anything at all -- any OTHER season in its own
 * chain, listed or not (`apply_unlink`'s own "already a series of one"
 * check, mirrored for display). `[]` for a series of one (or missing series
 * data) reads as not linked, the same "collapse to the plain case"
 * `SeasonTabStrip`'s own header describes for the identical input shape. */
function isLinkedToSomething(series) {
  return seriesSeasonsOf(series).length > 1
}

/** This season's immediate neighbours in series order (root to tail,
 * `compute_watch_series`'s own order) -- `apply_unlink`'s own chain-index
 * walk, read from data already on the page rather than re-derived from a
 * second grouping (gotcha 21: display-only, see the module header). */
function neighborsOf(series, subscriptionId) {
  const seasons = seriesSeasonsOf(series)
  const idx = seasons.findIndex((season) => season.id === subscriptionId)
  if (idx === -1) return { previous: null, next: null }
  return {
    previous: idx > 0 ? seasons[idx - 1] : null,
    next: idx < seasons.length - 1 ? seasons[idx + 1] : null,
  }
}

function unlinkConfirmText(subscription, series) {
  const { previous, next } = neighborsOf(series, subscription?.id)
  const name = typeof subscription?.title === 'string' ? subscription.title : 'This season'
  if (previous && next) {
    return `Take ${name} out of its series? ${previous.title} will link to ${next.title} instead.`
  }
  if (next) {
    return `Take ${name} out of its series? ${next.title} will become the start of its series.`
  }
  if (previous) {
    return `Take ${name} out of its series? ${previous.title} will no longer have a next season.`
  }
  return `Take ${name} out of its series?`
}

function UnlinkControl({ subscription, series, onChanged }) {
  const request = useWatchRequest()
  const [confirming, setConfirming] = useState(false)

  // `sequel` is the "next" direction only (`FranchiseLinkControl.jsx`'s own
  // header on why this page reads it rather than re-deriving from
  // `franchise`) -- so this check is a genuine confirmation whenever the
  // unlinked season HAD a next season, and a harmless vacuous one when it
  // only had a previous (nothing to observe changing in this one field,
  // never a WRONG signal -- `useConfirmAfterApplied`'s own contract: a
  // weak confirmation reads as an honest addendum, never a claim the press
  // itself failed).
  const isConfirmed = (shared) => (
    (subscriptionsOf(shared).find((row) => row.id === subscription?.id)?.sequel ?? null) === null
  )
  const confirmFailed = useConfirmAfterApplied(request, onChanged, isConfirmed)

  const press = () => {
    setConfirming(false)
    request.ask({ kind: KIND_UNLINK_SEASON, target: subscription.id, payload: {} })
  }

  if (request.status) {
    return (
      <div className="d2d-watchtitle__franchiselink" data-testid="manage-unlink">
        <ActStrip
          request={request}
          confirmFailed={confirmFailed}
          testId="manage-unlink-status"
          confirmStaleTestId="manage-unlink-confirm-stale"
          doneText={UNLINK_DONE_TEXT}
          command={unlinkCommand(subscription?.title)}
          onRetry={press}
        />
      </div>
    )
  }

  if (confirming) {
    return (
      <div className="d2d-watchtitle__franchiselink" data-testid="manage-unlink">
        <p className="d2d-watch__flatline" data-testid="manage-unlink-confirm-text">
          {unlinkConfirmText(subscription, series)}
        </p>
        <div className="d2d-watchtitle__franchiselinkbtns">
          <button
            type="button"
            className="d2d-watchtitle__franchiselinkbtn"
            data-testid="manage-unlink-confirm-yes"
            onClick={press}
          >
            Unlink
          </button>
          <button
            type="button"
            className="d2d-watchtitle__franchiselinkcancel"
            data-testid="manage-unlink-confirm-keep"
            onClick={() => setConfirming(false)}
          >
            Keep
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="d2d-watchtitle__franchiselink" data-testid="manage-unlink">
      <div className="d2d-watchtitle__franchiselinkbtns">
        <button
          type="button"
          className="d2d-watchtitle__franchiselinkbtn"
          data-testid="manage-unlink-open"
          onClick={() => setConfirming(true)}
        >
          Take this season out of its series
        </button>
      </div>
    </div>
  )
}

/**
 * The title page's own "Manage" section -- mounted by both `WatchTitle.jsx`'s
 * `WatchTitleBody` (a listed title) and `SeriesSeasonView` (P6-298b's
 * unlisted-season view). Renders nothing at all unless the season is linked
 * to something (see the module header) or the viewer is signed out.
 *
 * `subscription`/`series`/`you`/`onChanged` are the caller's own READY/SEASON
 * fields, passed straight through the way `Hero`'s own props already are.
 */
export function ManagePanel({ subscription, series, you, onChanged }) {
  if (!you?.signedIn) return null
  if (!isLinkedToSomething(series)) return null

  return (
    <section className="d2d-watch__section d2d-watchtitle__manage" data-testid="watch-title-manage">
      <span className="d2d-micro d2d-watchtitle__sectiontitle">Manage</span>
      <UnlinkControl subscription={subscription} series={series} onChanged={onChanged} />
    </section>
  )
}

export default ManagePanel
